/**
 * Allowlist sanitizer for OPERATOR-AUTHORED rich HTML — the content widget's
 * SEO copy (#1264, collection pages D3). Sibling of `common/html-safe.ts`
 * (#963), which guards PMS-supplied property descriptions; the two differ on
 * purpose and are kept separate so a change to one channel's policy cannot
 * quietly widen the other:
 *
 * - Narrower allowlist. SEO copy is paragraphs, emphasis, lists, sub-headings
 *   and links — nothing else. No images, tables, spans/divs, inline style or
 *   h1 (the page's H1 belongs to the host page / property title, not to
 *   pasted copy).
 * - Links stay in-tab. Collection copy links to the operator's OWN pages
 *   (other collections, the listings page), so `target="_blank"` and
 *   `nofollow` — right for untrusted PMS text — would be wrong here. Only
 *   `rel="noopener"` is forced.
 * - Entities are DECODED before the URL scheme check, so an entity-obfuscated
 *   `&#106;avascript:` href is dropped outright rather than emitted as an
 *   inert-but-confusing relative link.
 *
 * Built on `js-xss` (the `xss` package, already a direct dependency): pure
 * string processing, no DOM, so the SAME function runs in the widget's
 * `renderToString` SSR path, the browser mount and plain Node tests. The
 * client is not a security boundary (a compromised dashboard account is the
 * injection source), so sanitizing at the render boundary — not only at
 * save time — is what keeps every visitor safe regardless of what is stored.
 *
 * Two entry points. `sanitizeContentHtml` is the content widget's policy.
 * `createContentSanitizer(policy)` is the policy factory: it builds a
 * sanitizer for one caller from a ContentHtmlPolicy (allowlist, attributes,
 * `rel`, input cap, rooted-only relative hrefs, the html tokenizer, tag
 * balancing), every option defaulting to the content widget's behaviour.
 * The bedroom-details path (common/bedroom-showcase.ts, #1518) is the
 * caller with a non-default policy.
 */
// Default import, not named: js-xss is CommonJS with helpers attached to
// `module.exports`, and cjs-module-lexer does not surface them all as ESM
// named exports (see common/html-safe.ts for the history).
import xssModule from 'xss'
const { FilterXSS } = xssModule as unknown as typeof import('xss')

/** Tags operator copy may contain. Everything else is removed (not escaped). */
export const CONTENT_HTML_ALLOWED_TAGS = [
  'p', 'br',
  'strong', 'b', 'em', 'i',
  'ul', 'ol', 'li',
  'h2', 'h3', 'h4',
  'a',
] as const

/** Attributes kept per tag. `rel` is not listed: it is forced on afterwards. */
const ALLOWED_ATTRS: Record<string, string[]> = {
  a: ['href', 'title'],
}

/**
 * Link schemes an explicit scheme must be on. A relative URL (no scheme) is
 * allowed. Allowlisting — rather than blocking `javascript:` — cannot be
 * bypassed by inventing a scheme spelling the blocklist did not anticipate.
 * `http` is included alongside `https`: an operator pasting a plain-http
 * internal link gets a working link, and http is no XSS vector.
 */
export const CONTENT_HREF_SCHEMES = ['http', 'https', 'mailto', 'tel'] as const

/**
 * Elements whose TEXT CONTENT is dropped along with the tag. Only elements
 * that HAVE a body: js-xss replaces a listed opener with a `[removed]`
 * marker and only erases opener→closer once the closer arrives, so a void
 * tag here (`<embed>`) would leave the marker in the output as visible text.
 * Void tags are simply removed by `stripIgnoreTag`. Shared with
 * ./inline-html (#1163), which reuses this drop-body policy verbatim. This
 * is the list the content widget shipped with; a policy may widen it
 * (ContentHtmlPolicy.dropBodyTags), which is where the bedroom-details path
 * adds the media elements whose fallback text #1497 dropped.
 */
export const DROP_BODY_TAGS = [
  'script', 'style', 'iframe', 'object', 'svg', 'math',
  'template', 'noscript', 'textarea', 'title', 'xmp', 'noembed', 'noframes',
]

const DROP_BODY_TAG_SET = new Set(DROP_BODY_TAGS)
const TAG_NAME_RE = /^<(\/?)([a-zA-Z][a-zA-Z0-9-]*)/
const SELF_CLOSING_RE = /\/\s*>$/
/**
 * The only elements on which HTML honours a self-closing `/>`: foreign
 * content (SVG, MathML). On every other element the slash is ignored, so
 * `<script/>alert(1)</script>` is a real script whose body runs (#1518,
 * Eli's cold pass): under the html tokenizer a drop-body opener written
 * that way still opens its region here (ContentTokenizer rule 7). The js-xss
 * reading keeps any `/>` self-closing, which is what the content widget
 * shipped with (the body is then escaped text, never a script).
 */
const FOREIGN_TAG_SET = new Set(['svg', 'math'])
/** After `<`, only these start markup in the HTML data state; anything else leaves the `<` as text. */
const MARKUP_START_RE = /[A-Za-z/!?]/
/** Where HTML ends a tag NAME: whitespace, `/` or `>` (tag-name state). */
const TAG_NAME_END_RE = /[ \t\n\f\r/>]/

/**
 * Rule 5's second half, html only: a closer ends its region only when its
 * name is COMPLETE. TAG_NAME_RE matches a PREFIX of the name, so
 * `</script:x>` was read as a `script` closer; HTML ends a tag name at
 * whitespace, `/` or `>`, so `:x` makes it a DIFFERENT element and a browser
 * keeps the script region open. That prefix read published
 * `<script></script:x><a href=/evil>click</a></script>` as a live link where
 * the retired sanitizer emitted none (#1562 row 8, the one shape this module
 * was worse on than the tokenizer it replaced). The retired oracle's own
 * closer regex already required `[\s/>]` here. Resolved in this tokenizer's
 * usual direction: an incomplete closer changes nothing, the region stays
 * open, and more is dropped. Openers are deliberately NOT held to the same
 * rule — treating `<script:x>` as a region opener drops more, and tightening
 * it would let its body through.
 */
function closesRegion(tagText: string, name: RegExpExecArray): boolean {
  const after = tagText[name[0].length]
  return after === undefined || TAG_NAME_END_RE.test(after)
}

/**
 * How `guardContentTags` reads the input.
 *
 * - `js-xss` (default): exactly js-xss's reading, so the guard sees every tag
 *   js-xss will see and nothing else. Any `<` starts a tag; comments are left
 *   for js-xss's own regex strip.
 * - `html`: the HTML tokenizer's reading of the shapes js-xss reads
 *   differently, each resolved in the direction of removing more or escaping
 *   more, never less, and each emitted in a form js-xss then reads the same
 *   way:
 *     1. a `<` not followed by a letter, `/`, `!` or `?` is text (`1 < 2`
 *        keeps its `<`, emitted as `&lt;`, instead of swallowing the sentence
 *        up to the next `>`);
 *     2. a `<!--` at a tag-start position is a comment consumed whole to its
 *        `-->` and dropped, at any depth (so a `<!--` inside a tag or a
 *        quoted attribute value is tag bytes, never a splice point);
 *     3. inside a tag, a `<` outside quotes is part of the tag (an attribute
 *        name character), not a restart: `<scr<script>ipt>` is one tag named
 *        `scr`. Emitted as `&lt;` so js-xss cannot restart on it either, and
 *        so the guard's output carries exactly one raw `<` per tag it emits;
 *     4. a tag cut off by the end of the input is dropped (eof-in-tag), not
 *        escaped into visible text;
 *     5. a drop-body region ends only at a closer of its own NAME, and only
 *        at a COMPLETE one: `<template>x</svg>y</template>` keeps `y` inside
 *        the template, and `</script:x>` is not a `script` closer at all
 *        (see closesRegion), both as HTML does. (js-xss mode: any drop-body
 *        closer ends the innermost region, which is what the content
 *        widget's tests pin down.);
 *     6. attributes are read the way HTML reads them: a `/` between
 *        attributes is a separator (`<a h/ref="/x">` carries the attributes
 *        `h` and `ref`, never `href`), and an attribute whose name holds a
 *        character js-xss deletes before its allowlist match (`hr"ef`,
 *        `h&ref`: js-xss reads both as `href`; a browser reads neither so)
 *        is dropped whole, name and value. See readAttrsAsHtml;
 *     7. a self-closing `/>` closes only a foreign element (`<svg/>`,
 *        `<math/>`); on any other drop-body opener the slash is ignored and
 *        the region opens, so `<script/>alert(1)</script>` loses its body
 *        (#1518, Eli's cold pass). The js-xss reading keeps any `/>`
 *        self-closing, as the content widget shipped;
 *     8. of duplicate attributes the first wins (`<a href="/a" href="/b">`
 *        links `/a`), as a browser reads them; the js-xss reading emits both,
 *        as js-xss does.
 *   A policy using this mode must also set js-xss's `allowCommentTag`, or its
 *   regex comment strip runs over the output anyway.
 */
export type ContentTokenizer = 'js-xss' | 'html'

/** End of the comment opening at `lt` (`<!--`), per the HTML comment states; unterminated runs to the end. */
function commentEnd(html: string, lt: number): number {
  const body = lt + 4
  // Abrupt closes: `<!-->` and `<!--->` are complete (empty) comments.
  if (html.startsWith('>', body)) return body + 1
  if (html.startsWith('->', body)) return body + 2
  // One forward scan for either close (`-->` or `--!>`). Two indexOf calls
  // each walked the whole remaining input for every comment, quadratic over
  // a run of comments (round-1 review: 77 ms for 2500 of them at the
  // bedroom cap).
  for (let k = body; k + 2 < html.length; k++) {
    if (html[k] !== '-' || html[k + 1] !== '-') continue
    if (html[k + 2] === '>') return k + 3
    if (html[k + 2] === '!' && html[k + 3] === '>') return k + 4
  }
  return html.length
}

/**
 * True when the quote at `pos` OPENS an attribute value under js-xss's rule
 * (`parseTag` in xss/lib/parser.js): only a quote whose previous
 * non-whitespace character is `=`. Any other quote is ordinary tag text.
 */
function opensAttrValue(html: string, pos: number): boolean {
  let k = pos - 1
  while (k >= 0 && html[k].trim() === '') k--
  return k >= 0 && html[k] === '='
}

/**
 * One linear pass that tokenizes tags EXACTLY the way js-xss's parser does —
 * so it sees every tag js-xss will see, and nothing else — and applies two
 * structural guards before js-xss runs:
 *
 * 1. Tag length. Any tag region longer than MAX_TAG_LENGTH is dropped whole.
 *    js-xss's attribute parser is quadratic in a tag's attribute run, and
 *    the tag region it measures honours quoted values (a `>` inside
 *    `title="a > b"` does not end the tag), so a naive `[^>]{n,}` regex was
 *    bypassed by exactly that (cold-pass review: 24 s again).
 * 2. Drop-body elements with their content, depth-aware. js-xss's own
 *    `stripIgnoreTagBody` is single-state: nested `<script><style>x</style>
 *    AFTER</script>` re-emits AFTER with a `[removed]` marker, and an unclosed
 *    opener leaves the marker plus the script text. Open drop-body elements
 *    sit on a stack: an opener pushes its name, a closer pops (the innermost
 *    region in js-xss mode; in html mode the nearest region of the closer's
 *    own name and everything inside it, see ContentTokenizer rule 5), a
 *    self-closing opener is removed without opening a region (js-xss
 *    reading: any `/>`; html reading: only `<svg/>` / `<math/>`, the
 *    elements HTML lets `/>` close, see FOREIGN_TAG_SET), and only
 *    text/tags outside every region survive.
 *    The stack bookkeeping runs BEFORE the length guard: js-xss opens a
 *    script region on `<script ` however long the opener is, so an overlong
 *    drop-body opener or closer still moves the stack and is then discarded
 *    (round-3 review: an overlong `<script …>` used to leave its body as
 *    visible text, an overlong `</script …>` used to swallow all later copy).
 *
 * js-xss tokenization rules mirrored here: a tag starts at `<`; a quote opens
 * a value only when preceded by `=` (see opensAttrValue); inside a value the
 * tag does not end at `>`; OUTSIDE a value a second `<` restarts the tag (the
 * partial text before it is escaped as text — kept here for js-xss to
 * escape); the tag ends at `>` or at the end of the input.
 *
 * `tokenizer: 'html'` (see ContentTokenizer) is applied here for rules 1 to
 * 7: rules 1 and 2 in front of that scan (a `<` that cannot start markup
 * is emitted as `&lt;`; a comment is consumed whole and dropped, at whatever
 * depth), rule 3 inside the scan (an unquoted `<` within a tag is tag text,
 * emitted as `&lt;` instead of restarting the tag), rule 4 at the tag's end
 * (a tag cut off by the end of the input is dropped), rule 5 in the
 * drop-body stack (a closer ends the nearest open region of its own name),
 * rule 6 on every emitted opener (readAttrsAsHtml rewrites its attribute
 * run as HTML reads it) and rule 7 on a self-closing drop-body opener (only
 * a foreign element honours the `/>`). Rule 8 (duplicate attributes) lives
 * in createContentSanitizer's onTagAttr.
 *
 * `dropBody` is the set of elements removed with their content: the shared
 * DROP_BODY_TAGS unless the policy widens it.
 */
export function guardContentTags(
  html: string,
  tokenizer: ContentTokenizer = 'js-xss',
  dropBody: ReadonlySet<string> = DROP_BODY_TAG_SET,
): string {
  const n = html.length
  // Open drop-body elements, innermost last. Text and tags survive only while
  // this is empty.
  const open: string[] = []
  // html reading: where each open element sits in `open`, so a closer finds
  // its element without a scan of the stack (a run of stray closers after a
  // run of openers was quadratic; cold-pass review). Same shape as
  // balanceContentTags.
  const openAt = new Map<string, number[]>()
  const enter = (tag: string) => {
    if (tokenizer === 'html') {
      const list = openAt.get(tag)
      if (list) list.push(open.length)
      else openAt.set(tag, [open.length])
    }
    open.push(tag)
  }
  const leave = (tag: string) => {
    if (tokenizer === 'html') {
      // rule 5: a closer ends the nearest open element of its own name (and
      // everything opened inside it); with nothing of that name open it is
      // a stray closer and changes nothing.
      const list = openAt.get(tag)
      if (!list || list.length === 0) return
      const idx = list[list.length - 1]
      while (open.length > idx) openAt.get(open.pop()!)!.pop()
    } else if (open.length > 0) {
      // js-xss reading: any drop-body closer ends the innermost region.
      open.pop()
    }
  }
  let out = ''
  let i = 0
  while (i < n) {
    const lt = html.indexOf('<', i)
    if (lt === -1) {
      if (open.length === 0) out += html.slice(i)
      break
    }
    if (open.length === 0) out += html.slice(i, lt)
    if (tokenizer === 'html') {
      const next = html[lt + 1]
      if (next === undefined || !MARKUP_START_RE.test(next)) {
        // HTML data state: this `<` is text. Encoded, not passed through raw,
        // because js-xss WOULD open a tag on it.
        if (open.length === 0) out += '&lt;'
        i = lt + 1
        continue
      }
      if (html.startsWith('<!--', lt)) {
        i = commentEnd(html, lt)
        continue
      }
    }
    // Scan to the tag's end with js-xss's rules.
    let j = lt + 1
    let quote: string | null = null
    let restart = false
    // html tokenizer: positions of unquoted `<` inside this tag (rule 3).
    const innerLts: number[] = []
    while (j < n) {
      const c = html[j]
      if (quote) {
        if (c === quote) quote = null
      } else if (c === '<') {
        if (tokenizer === 'html') {
          innerLts.push(j)
        } else {
          restart = true
          break
        }
      } else if (c === '>') {
        break
      } else if ((c === '"' || c === "'") && opensAttrValue(html, j)) {
        quote = c
      }
      j++
    }
    if (j >= n && tokenizer === 'html') break // rule 4: eof-in-tag, the tag is dropped and nothing follows it
    if (restart) {
      // `<a x" <script>`: js-xss escapes `<a x" ` as text and starts a new
      // tag at the second `<`. Keep the partial as text (it is not a tag).
      const partial = html.slice(lt, j)
      const partialName = TAG_NAME_RE.exec(partial)
      if (partialName && dropBody.has(partialName[2].toLowerCase())) {
        // ...unless the partial NAMES a drop-body element. Emitting `<script `
        // as text put a raw `<` back into the string js-xss then re-parses, and
        // it read the fragment as a real script tag: `<script <svg>` came out as
        // a literal `[removed]` marker, and `<p>a</p><script <svg>x</svg><p>b</p>`
        // rendered a visible `<script ` between the paragraphs (#1264 review;
        // 2092/150k fuzz cases). Treat it as the opener/closer it names and drop
        // it — the same thing already done for an unterminated `<script>`, and
        // wrong only in the direction of removing more.
        if (partialName[1] === '/') leave(partialName[2].toLowerCase())
        else enter(partialName[2].toLowerCase())
      } else if (open.length === 0) {
        out += partial
      }
      i = j
      continue
    }
    const tagEnd = j < n ? j + 1 : n // unterminated → the tag runs to the end
    const tagText = html.slice(lt, tagEnd)
    i = tagEnd
    const name = TAG_NAME_RE.exec(tagText)
    const tagName = name ? name[2].toLowerCase() : ''
    if (name && dropBody.has(tagName)) {
      // guard 2 (before guard 1: an overlong opener/closer still moves the stack)
      if (name[1] === '/') {
        if (tokenizer !== 'html' || closesRegion(tagText, name)) leave(tagName) // rule 5 under html
      }
      else if (tokenizer === 'html' ? !(FOREIGN_TAG_SET.has(tagName) && SELF_CLOSING_RE.test(tagText)) : !SELF_CLOSING_RE.test(tagText)) enter(tagName) // rule 7 under html
      continue // the tag itself is never emitted, whatever its length
    }
    if (tagText.length > MAX_TAG_LENGTH) continue // guard 1: dropped whole
    if (open.length === 0) {
      let piece: string
      if (innerLts.length === 0) {
        piece = tagText
      } else {
        // rule 3: every unquoted inner `<` becomes `&lt;`, so js-xss sees one tag here too.
        piece = ''
        let from = lt
        for (const at of innerLts) {
          piece += html.slice(from, at) + '&lt;'
          from = at + 1
        }
        piece += html.slice(from, tagEnd)
      }
      // rule 6: an opener's attributes, as HTML reads them.
      if (tokenizer === 'html' && name && name[1] !== '/') piece = readAttrsAsHtml(piece)
      out += piece
    }
  }
  return out
}

/**
 * Rule 6 of the html tokenizer. js-xss splits a tag's attribute run on
 * whitespace and `=` only, then DELETES every character outside
 * `[a-zA-Z0-9\_:.-]` from each name before the allowlist match
 * (xss/lib/parser.js, REGEXP_ILLEGAL_ATTR_NAME). So `<a h/ref="/evil"
 * href="/good">` reached js-xss as an `href` of `/evil` followed by another
 * href, and first-attribute-wins emitted `/evil`; a browser, and #1497's
 * tokenizer, read `h` and `ref` and linked `/good` (round-1 review). Same
 * for `hr"ef` and `h&ref`. Rewriting the run here, per the HTML attribute
 * states, means js-xss can only see the attributes a browser sees:
 *
 * - a `/` outside a value separates attributes (self-closing start tag
 *   state, reconsumed); `<br/>` comes out as `<br>`, which the balance pass
 *   canonicalised anyway;
 * - an attribute whose HTML name holds a character js-xss would delete is
 *   dropped whole, name and value: it can never be the attribute a browser
 *   reads, and dropping is the direction this tokenizer always resolves in;
 * - a `>` met in a name or an unquoted value is where HTML ended the tag
 *   (the guard's scan finds the end with js-xss's quote rule, which opens a
 *   quote after ANY `=`): that attribute and the rest of the run are dropped;
 * - everything else is emitted as `name`, `name="value"` or `name='value'`,
 *   the value text as the input carried it; an unquoted value is re-emitted
 *   double-quoted (a `"` inside it as `&quot;`), because js-xss reads a
 *   trailing `/` in an unquoted value as self-closing syntax and cut
 *   `href=/rooms/` to `/rooms` (round-2 review) where HTML keeps the slash.
 *
 * `tag` is one opener from the guard's scan under the html tokenizer: it
 * starts with `<` and a tag name and ends with the `>` that closed it (rule
 * 4 dropped any tag cut off by the input's end). Linear in the tag.
 */
const JSXSS_ILLEGAL_ATTR_NAME_RE = /[^a-zA-Z0-9\\_:.-]/
const ATTR_SPACE_RE = /[ \t\n\f\r]/
function readAttrsAsHtml(tag: string): string {
  if (tag[1] === '/') return tag
  const end = tag.length - 1 // the closing `>`
  // The tag name as HTML reads it: up to whitespace, `/` or the end. Wider
  // than TAG_NAME_RE on purpose: `<a:b href="...">` is the element `a:b`, and
  // js-xss reads it that way too; taking `a` here would have turned it into
  // an anchor carrying a `:b` attribute.
  let i = 1
  while (i < end && tag[i] !== '/' && !ATTR_SPACE_RE.test(tag[i])) i++
  let out = tag.slice(0, i)
  while (i < end) {
    const c = tag[i]
    if (c === '/' || ATTR_SPACE_RE.test(c)) {
      i++
      continue
    }
    // attribute name: up to whitespace, `/`, `=` or the tag's end. A `=` in
    // FIRST position is part of the name (HTML's before-attribute-name state:
    // unexpected-equals-sign-before-attribute-name starts an attribute whose
    // name begins with it). Reading it as "empty name, then a value" made
    // `<a =x"= href="/evil">` lose `x"=` as that value and then emit the
    // `href`, a live link where HTML sees one attribute named `=x"` whose
    // VALUE is `href="/evil"` and the retired sanitizer emitted inert text
    // (fresh pass 2). The name then holds a character js-xss would delete, so
    // the attribute is dropped whole, value included.
    let attrName = ''
    if (tag[i] === '=') attrName += tag[i++]
    while (i < end && tag[i] !== '/' && tag[i] !== '=' && !ATTR_SPACE_RE.test(tag[i])) attrName += tag[i++]
    // A `>` in a NAME is where HTML ended the tag. The guard's scan found this
    // tag's end with js-xss's quote rule (a quote opens after any `=`), so in
    // `<a ="> href=/evil x">` it ran on to the second `>`; HTML reads `="` as
    // an attribute name, ends the tag at the first `>`, and the rest is text.
    // Reading that rest as attributes emitted a live `/evil` link no browser
    // sees (cold pass on 73425b1d). Stop here: this attribute and everything
    // after it are dropped, the direction this tokenizer always resolves in.
    if (attrName.includes('>')) break
    // after the name: whitespace, then `=` opens a value; anything else starts the next attribute
    let k = i
    while (k < end && ATTR_SPACE_RE.test(tag[k])) k++
    let value = ''
    if (tag[k] === '=') {
      k++
      while (k < end && ATTR_SPACE_RE.test(tag[k])) k++
      const q = tag[k]
      if (q === '"' || q === "'") {
        const close = tag.indexOf(q, k + 1)
        const stop = close === -1 || close >= end ? end : close + 1
        value = tag.slice(k, stop)
        k = stop
      } else {
        let stop = k
        while (stop < end && !ATTR_SPACE_RE.test(tag[stop])) stop++
        const raw = tag.slice(k, stop)
        // Same rule for an UNQUOTED value: in `<a x=="> href=/evil y">` and
        // `<a x=y="> href=/evil z">` HTML is already inside x's unquoted value
        // when the quote arrives, so the first `>` ends the tag there too.
        if (raw.includes('>')) break
        value = '"' + raw.replace(/"/g, '&quot;') + '"'
        k = stop
      }
      i = k
    }
    if (attrName === '' || JSXSS_ILLEGAL_ATTR_NAME_RE.test(attrName)) continue
    out += ' ' + attrName + (value === '' ? '' : '=' + value)
  }
  return out + '>'
}

/**
 * Size guards — js-xss's attribute parser is quadratic in the length of a
 * single tag's attribute run: a `<a x x x … href>` tag with 64 KB of
 * attributes took 22 s to process (Codex review, 2026-08-27), which would
 * block every SSR cache miss and CSR mount for a compromised-operator
 * payload. Legitimate copy never has a tag over a few hundred bytes (a link
 * is `<a href="…" title="…">`), so a tag longer than MAX_TAG_LENGTH is
 * dropped whole before parsing (linear scan), and the body is capped at
 * MAX_CONTENT_HTML_LENGTH. The AGGREGATE worst case is then linear in the
 * tag cap (N tags × L² with N·L ≤ the body cap → body × L): 64 tags just
 * under 1 KB cost ~350–700 ms, at 512 B about half that (Codex round 2).
 * Both guards are deterministic, so SSR and CSR still agree.
 */
export const MAX_CONTENT_HTML_LENGTH = 65_536
export const MAX_TAG_LENGTH = 512
/** Largest per-byte growth any escape step can produce (`"` → `&quot;`). */
export const MAX_CONTENT_HTML_EXPANSION = 6

/**
 * Cap the INPUT body. The tag-length guard lives in `guardContentTags` (it
 * needs js-xss's own tokenization to be bypass-proof). Exported for tests.
 *
 * Bound on the OUTPUT (conservative, escape-aware): js-xss escapes `<` and
 * `>` in text (4 bytes each) and `escapeAttr` expands `&`, `"`, `<`, `>` in
 * attribute values (up to 6 bytes for `&quot;`), so every input byte becomes
 * at most MAX_CONTENT_HTML_EXPANSION output bytes; `hardenLinks` then adds
 * ` rel="noopener"` (15 bytes) per surviving anchor. Output length is at most
 * MAX_CONTENT_HTML_EXPANSION × MAX_CONTENT_HTML_LENGTH + 15 × anchors; since
 * an anchor needs at least three input bytes (`<a>`), the absolute ceiling is
 * (MAX_CONTENT_HTML_EXPANSION + 5) × MAX_CONTENT_HTML_LENGTH ≈ 704 KB. A body
 * made entirely of quote-dense attribute values lands near 400 KB; plain copy
 * stays within a few bytes of its input. Sanitizing an output again is
 * idempotent below the cap; at the cap an escape-dense body re-truncates —
 * accepted: the widget always sanitizes the STORED body, never its own output.
 */
export function boundContentHtml(value: string, maxLength: number = MAX_CONTENT_HTML_LENGTH): string {
  if (value.length <= maxLength) return value
  const capped = value.slice(0, maxLength)
  // The cut may land inside a tag; a half tag would be escaped into visible
  // text by js-xss, so drop the dangling fragment.
  const lastOpen = capped.lastIndexOf('<')
  return lastOpen > capped.lastIndexOf('>') ? capped.slice(0, lastOpen) : capped
}

// Named references an attacker can use to smuggle a scheme past a scheme
// regex (`javascript&colon;`, `&NewLine;javascript:`), plus the basic five.
// Lookup is case-insensitive on purpose: over-decoding only makes the scheme
// check STRICTER (a decoded value can only match more schemes, never fewer).
const NAMED_ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'",
  nbsp: ' ', tab: '\t', newline: '\n', colon: ':', sol: '/',
  num: '#', lpar: '(', rpar: ')', semi: ';', excl: '!', quest: '?',
  equals: '=', period: '.', comma: ',',
}

/**
 * Decode HTML character references (`&#106;`, `&#x6A;`, `&colon;`) in an
 * ATTRIBUTE value, the way a browser's named-character-reference state does.
 * Numeric references decode with or without the trailing semicolon; out-of-range
 * code points decode to nothing; unknown named references are left as-is.
 *
 * A NAMED reference missing its semicolon is NOT decoded when the next
 * character is `=` or ASCII alphanumeric — the spec's attribute carve-out
 * (13.2.5.73). Without it a perfectly ordinary pasted link lost a query
 * parameter: `?p=1&num=2` decoded to `?p=1#=2`, and the decoded value is the
 * one emitted, so the link shipped broken (#1264 review). Nineteen names are
 * in the table (`num`, `sol`, `colon`, `comma`, `period`, `equals`, `tab`, …),
 * several of which are plausible parameter names.
 *
 * The carve-out cannot widen what the scheme check accepts: it only declines to
 * decode in exactly the positions a browser also declines to, so the string
 * checked here is the string the browser will resolve.
 */
export function decodeEntities(value: string): string {
  return value.replace(
    /&(#[xX][0-9a-fA-F]+|#[0-9]+|[a-zA-Z][a-zA-Z0-9]*)(;?)/g,
    (match, body: string, semi: string, offset: number) => {
      if (body[0] === '#') {
        const hex = body[1] === 'x' || body[1] === 'X'
        const codePoint = hex ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10)
        // The range guard is what keeps fromCodePoint from throwing.
        if (!Number.isFinite(codePoint) || codePoint <= 0 || codePoint > 0x10ffff) return ''
        return String.fromCodePoint(codePoint)
      }
      if (semi === '') {
        const next = value[offset + match.length]
        if (next === '=' || (next !== undefined && /[0-9a-zA-Z]/.test(next))) return match
      }
      const named = NAMED_ENTITIES[body.toLowerCase()]
      return named === undefined ? match : named
    },
  )
}

// HTML-escape an attribute value so a quote/angle-bracket can never break out
// of the double-quoted attribute js-xss wraps it in.
function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * The href to emit for a link, or null when the link must lose its href.
 * Decodes entities first, then strips the ASCII control/whitespace characters
 * browsers ignore inside a scheme (`java\tscript:`), then allowlists the
 * scheme. Returns the decoded, trimmed value (not the stripped one) so a
 * legitimate URL keeps its internal characters intact.
 */
export function safeContentHref(raw: string): string | null {
  const decoded = decodeEntities(raw)
  const stripped = decoded.replace(/[\x00-\x20\x7f]+/g, '')
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(stripped)
  if (scheme && !(CONTENT_HREF_SCHEMES as readonly string[]).includes(scheme[1].toLowerCase())) {
    return null
  }
  const trimmed = decoded.trim()
  return trimmed === '' ? null : trimmed
}

/**
 * A scheme-less href under `relativeHrefs: 'rooted'`: a same-origin rooted
 * path (`/rooms`, but not the protocol-relative `//host` or its `/\host`
 * spelling, which browsers read as `//host` for http(s)) or a fragment. An
 * explicit scheme has already passed the allowlist in safeContentHref. And
 * nothing in the value may be whitespace, a control byte or a quote: those
 * never belong in a URL and are what a downstream consumer mis-splits on
 * (#1497's rule, kept). Whitespace is the `\s` class #1497 tested, which is
 * Unicode (U+00A0, U+2028, U+3000, ...) and not only the ASCII range the
 * control-byte class covers: a href holding a no-break space drops the link
 * here, before a caller's whitespace post-pass could rewrite it into a plain
 * space inside the attribute.
 */
function isRootedHref(href: string): boolean {
  if (/[\x00-\x20\x7f"'\s]/.test(href)) return false
  if (/^[a-z][a-z0-9+.-]*:/i.test(href)) return true
  return /^(?:\/(?![/\\])|#)/.test(href)
}

/**
 * Force `rel` on every surviving anchor. `rel` is not an allowlisted
 * attribute, so no sanitized anchor carries one yet, and js-xss escapes `<`
 * in text nodes, so `<a` here can only be a real tag.
 */
function hardenLinks(html: string, rel: string): string {
  return html.replace(/<a\b/gi, `<a rel="${rel}"`)
}

/**
 * Tags in sanitizer OUTPUT. The alphabet is closed: js-xss has removed or
 * escaped every `<` that is not an allowlisted tag, attribute values are
 * escaped by escapeAttr (no raw `<`, `>` or `"`), and text is escaped, so a
 * `[^<>]*` attribute run can only be what js-xss emitted.
 */
const OUTPUT_TAG_RE = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)([^<>]*)>/g
const VOID_TAGS = new Set(['br'])

/**
 * Balance sanitizer output the way #1497's tokenizer did (`balanceTags`):
 *
 * - every opener still open at the end is closed, innermost first, so no tag
 *   can reach the page unclosed (an unclosed `<a>` is re-opened by the HTML
 *   adoption agency around every later sibling, which let one bedroom's
 *   details swallow the next room card into the same attacker link — #1497
 *   round-2 HIGH);
 * - a closer with no matching opener is dropped; a closer whose opener is
 *   not on top closes through everything above it (implicit close, the way a
 *   parser's error recovery does; nothing is re-opened);
 * - an `<a>` that opens while an anchor is open closes that anchor first,
 *   through everything opened inside it, as HTML's adoption agency does:
 *   nested anchors never exist in a DOM. So an inner anchor that loses its
 *   href leaves its text outside the outer link, not inside it (cold-pass
 *   review: `<a href="https://x">X<a href="//y">Book</a></a>` had moved
 *   "Book" from `//y`, refused, into `https://x`);
 * - void `<br>` never takes a closer, and a source `<br/>` / `<p/>` (which
 *   js-xss re-emits as `<br />` / `<p />`) is canonicalised, since HTML
 *   ignores the slash on a non-void element;
 * - an `<a>` that lost its href (the URL was rejected) is unwrapped to its
 *   text. It still takes a stack position, so a nested real anchor's closer
 *   pairs by position alone (the round-1 HIGH was a hrefless anchor stealing
 *   a later real anchor's closer).
 *
 * Linear: each stack entry is pushed and popped once, and a closer finds its
 * opener through a per-tag position list rather than a scan of the stack.
 */
export function balanceContentTags(html: string): string {
  let out = ''
  let last = 0
  const stack: Array<{ tag: string; emit: boolean }> = []
  const positions = new Map<string, number[]>()
  const push = (tag: string, emit: boolean) => {
    const list = positions.get(tag)
    if (list) list.push(stack.length)
    else positions.set(tag, [stack.length])
    stack.push({ tag, emit })
  }
  const closeThrough = (tag: string): string => {
    const list = positions.get(tag)
    if (!list || list.length === 0) return '' // stray closer, no opener anywhere
    const idx = list[list.length - 1]
    let closers = ''
    while (stack.length > idx) {
      const entry = stack.pop()!
      positions.get(entry.tag)!.pop()
      if (entry.emit) closers += `</${entry.tag}>`
    }
    return closers
  }
  for (const m of html.matchAll(OUTPUT_TAG_RE)) {
    out += html.slice(last, m.index)
    last = m.index + m[0].length
    const tag = m[2].toLowerCase()
    const closing = m[1] === '/'
    const attrs = m[3].replace(/\s*\/$/, '')
    if (VOID_TAGS.has(tag)) {
      if (!closing) out += `<${tag}${attrs}>`
      continue
    }
    if (closing) {
      out += closeThrough(tag)
      continue
    }
    if (tag === 'a') out += closeThrough('a')
    const emit = tag !== 'a' || /\shref="/.test(attrs)
    push(tag, emit)
    if (emit) out += `<${tag}${attrs}>`
  }
  out += html.slice(last)
  while (stack.length > 0) {
    const entry = stack.pop()!
    if (entry.emit) out += `</${entry.tag}>`
  }
  return out
}

/**
 * What a caller may vary. Every option defaults to the content widget's
 * behaviour, so `createContentSanitizer()` with no policy IS
 * `sanitizeContentHtml`, byte for byte. The non-default values exist for
 * the bedroom-details path (common/bedroom-showcase.ts, #1518), which folded
 * its own tokenizer onto this module and needed its stricter rules kept.
 */
export type ContentHtmlPolicy = {
  /** Tags kept; everything else is removed. Default: CONTENT_HTML_ALLOWED_TAGS. */
  allowedTags?: readonly string[]
  /** Attributes kept per tag. Default: `{ a: ['href', 'title'] }`. */
  allowedAttrs?: Readonly<Record<string, readonly string[]>>
  /** The `rel` forced onto every surviving anchor. Default: `noopener`. */
  rel?: string
  /** Input cap, see boundContentHtml. Default: MAX_CONTENT_HTML_LENGTH. */
  maxLength?: number
  /**
   * `any` (default): a scheme-less href is a relative URL and kept as
   * written. `rooted`: see isRootedHref; a relative path without a leading
   * slash, a protocol-relative `//host`, or any whitespace/quote/control byte
   * in the value drops the href.
   */
  relativeHrefs?: 'any' | 'rooted'
  /** See ContentTokenizer. Default: `js-xss`. */
  tokenizer?: ContentTokenizer
  /**
   * Elements removed together with their content. Default: DROP_BODY_TAGS
   * (the content widget's list). The bedroom-details path widens it to the
   * media elements whose fallback text #1497 dropped.
   */
  dropBodyTags?: readonly string[]
  /**
   * See balanceContentTags. Default: false (js-xss's output as-is: unclosed
   * tags stay unclosed, a hrefless anchor stays a bare `<a>`). With it on,
   * `rel` rides on the href attribute instead of the prefix pass, because
   * every anchor that survives has one.
   */
  balanceTags?: boolean
}

/**
 * Build a sanitizer for operator-authored rich HTML headed for a
 * `dangerouslySetInnerHTML` sink. The returned function gives "" for
 * non-strings / blank input; otherwise the input with every non-allowlisted
 * tag and attribute removed, executable URL schemes dropped, and `rel` on
 * each link. Pure string processing (js-xss behind guardContentTags), so the
 * same function runs in SSR, the browser mount and Node tests.
 */
export function createContentSanitizer(policy: ContentHtmlPolicy = {}): (value: unknown) => string {
  const allowedTags = policy.allowedTags ?? CONTENT_HTML_ALLOWED_TAGS
  const allowedAttrs = policy.allowedAttrs ?? ALLOWED_ATTRS
  const rel = policy.rel ?? 'noopener'
  const maxLength = policy.maxLength ?? MAX_CONTENT_HTML_LENGTH
  const rooted = policy.relativeHrefs === 'rooted'
  const tokenizer = policy.tokenizer ?? 'js-xss'
  const balance = policy.balanceTags === true
  const dropBodyTags = policy.dropBodyTags ?? DROP_BODY_TAGS
  const dropBodySet: ReadonlySet<string> = new Set(dropBodyTags)

  // Attribute names seen on the tag js-xss is currently rebuilding. Under the
  // html tokenizer a tag that repeats an attribute (`<a href="/a" href="/b">`)
  // keeps the FIRST one, as browsers do (ContentTokenizer rule 8); the js-xss
  // reading emits both, as js-xss does and as the content widget shipped.
  // `process` is synchronous and never re-entered, so one set per filter is
  // enough.
  const htmlAttrs = tokenizer === 'html'
  let seenAttrs = new Set<string>()

  const filter = new FilterXSS({
    whiteList: Object.fromEntries(allowedTags.map((tag) => [tag, [...(allowedAttrs[tag] ?? [])]])),
    // Disallowed tags vanish (not escaped into visible `&lt;div&gt;` noise);
    // the listed ones take their text content with them.
    stripIgnoreTag: true,
    stripIgnoreTagBody: [...dropBodyTags],
    // Under the html tokenizer the guard has consumed every comment already.
    // js-xss's own strip is a plain regex over the whole string that deletes
    // `<!-- -->` bytes out of the MIDDLE of a quoted attribute value too, and
    // splices the halves into a value that was never in the input.
    allowCommentTag: tokenizer === 'html',
    onTag(_tag, _html, options) {
      if (!(options as { isClosing?: boolean }).isClosing) seenAttrs = new Set()
      return undefined
    },
    // Returning a string from onTagAttr replaces the attribute's entire
    // output — an empty string DROPS it (a bare `href` would otherwise be
    // emitted for a rejected URL, which links to the current page).
    onTagAttr(tag, name, value, isWhiteAttr) {
      if (!isWhiteAttr) return undefined // js-xss removes it (every on*, style, …)
      if (htmlAttrs && seenAttrs.has(name)) return ''
      seenAttrs.add(name)
      if (tag === 'a' && name === 'href') {
        const href = safeContentHref(value)
        if (href === null || (rooted && !isRootedHref(href))) return ''
        return balance ? `href="${escapeAttr(href)}" rel="${rel}"` : `href="${escapeAttr(href)}"`
      }
      return undefined // fall through to safeAttrValue below
    },
    safeAttrValue(_tag, _name, value) {
      // Remaining allowlisted attrs (`title`) are plain text: decode, then escape.
      return escapeAttr(decodeEntities(value).trim())
    },
  })

  return (value: unknown): string => {
    if (typeof value !== 'string') return ''
    const v = guardContentTags(boundContentHtml(value.trim(), maxLength), tokenizer, dropBodySet)
    if (v === '') return ''
    const processed = filter.process(v)
    return balance ? balanceContentTags(processed) : hardenLinks(processed, rel)
  }
}

const sanitizeContent = createContentSanitizer()

/**
 * Sanitize operator-authored rich HTML for a `dangerouslySetInnerHTML` sink.
 * Returns "" for non-strings / blank input; otherwise the input with every
 * non-allowlisted tag and attribute removed, executable URL schemes dropped,
 * and `rel="noopener"` on each link. The default policy of
 * createContentSanitizer.
 */
export function sanitizeContentHtml(value: unknown): string {
  return sanitizeContent(value)
}
